import React, { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import ModalWrapper from "../ModalWrapper";
import { CommonFooter } from "../FormComponent";
import LegalConsentCheckbox from "./LegalConsentCheckbox";
import { recordUserConsent } from "../../utils/api/consent_apis";
import { fetchUserLogin } from "../../utils/api";
import { setUser, setConsentModalRequired } from "../../features/auth/authSlice";
import { STATUS } from "../../utils/api/sharedfunction";
import { useSnackbar } from "../../utils/useSnackbar";
import { CONSENT_COPY } from "../../constants/consentCopy";
import styles from "../../styles/consent/legalConsent.module.scss";
import modalStyles from "../../styles/modalwrapper.module.scss";

const ReConsentModal = () => {
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const [consentMeta, setConsentMeta] = useState({ ready: false, agreed: false });
  const [submitting, setSubmitting] = useState(false);
  const [checkboxError, setCheckboxError] = useState(null);

  const handleConsentChange = useCallback((meta) => {
    setConsentMeta(meta);
    if (meta.agreed) {
      setCheckboxError(null);
    }
  }, []);

  const handleSubmit = async () => {
    if (!consentMeta.agreed) {
      setCheckboxError(CONSENT_COPY.reconsentValidationError);
      return;
    }

    if (!consentMeta.ready) {
      setCheckboxError(consentMeta.error || "Legal documents are not available. Please try again later.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await recordUserConsent({
        tosLegalDocumentVersionId: consentMeta.tosLegalDocumentVersionId,
        privacyLegalDocumentVersionId: consentMeta.privacyLegalDocumentVersionId,
      });

      if (response.status !== STATUS.SUCCESS) {
        showSnackbar(response.error || "Failed to record consent. Please try again.", "error");
        return;
      }

      const currentUser = await fetchUserLogin();
      if (currentUser?.status === 1 && currentUser?.data?.user) {
        dispatch(setUser({ user: currentUser.data.user }));
      }
      dispatch(setConsentModalRequired(false));
      setConsentMeta({ ready: false, agreed: false });
      setCheckboxError(null);
      showSnackbar(response.message || "Consent recorded successfully.", "success");
    } catch (error) {
      showSnackbar(error?.error || "Failed to record consent. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const buttons = [
    {
      label: "Accept and continue",
      onClick: handleSubmit,
      variant: "primary",
      className: "white bg-blue600",
      isdefault: true,
      loading: submitting,
      disabled: submitting,
    },
  ];

  return (
    <ModalWrapper
      isOpen
      title="Updated legal terms"
      size="medium"
      closeOnBackdrop={false}
      closeOnEsc={false}
      showCloseButton={false}
      type="info"
      footer={<CommonFooter buttons={buttons} align="right" />}
      border={false}
      headerClassName={modalStyles.modalHeaderConsent}
      backdropClassName={modalStyles.modalBackdropConsent}
    >
      <div className={styles.consentModalContent}>
      <p className={styles.consentBody}>{CONSENT_COPY.reconsentBody}</p>
      <p className={styles.consentHint}>{CONSENT_COPY.reconsentHint}</p>
      <LegalConsentCheckbox
        checked={consentMeta.agreed}
        onChange={handleConsentChange}
        disabled={submitting}
        error={checkboxError}
        id="reconsent-checkbox"
        emphasized
      />
      </div>
    </ModalWrapper>
  );
};

export default ReConsentModal;
